import type { RouteRecordRaw } from 'vue-router'

import { NAV_ITEMS } from './nav-items'

const AppShellLayout = () => import('@/layouts/AppShellLayout.vue')
const LoginView = () => import('@/views/LoginView.vue')
const OidcCallbackView = () => import('@/views/auth/OidcCallbackView.vue')
const PasskeyStepView = () => import('@/views/auth/PasskeyStepView.vue')
const MfaStepView = () => import('@/views/auth/MfaStepView.vue')
// CRM-2 / CRM-3 (CRM-0 Wave C, TC1.1-TC1.2): the real Customer 360 + billing
// ops screens, replacing MS-1's placeholders now that the crm backend
// (MS-2 / TB1.*) is live.
const CustomersView = () => import('@/views/customers/CustomersView.vue')
const BillingView = () => import('@/views/billing/BillingView.vue')
const ComingSoonView = () => import('@/views/placeholders/ComingSoonView.vue')
const ForbiddenView = () => import('@/views/ForbiddenView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
// CRM-6's real agent inbox — replaces the `support` nav item's
// ComingSoonView placeholder. TicketWorkspaceView isn't a NAV_ITEMS entry
// (there's no sidebar link for it), just a sibling detail route under the
// same `support:read` guard, added alongside navChildren below.
const SupportInboxView = () => import('@/views/support/InboxView.vue')
const SupportTicketWorkspaceView = () => import('@/views/support/TicketWorkspaceView.vue')
// CRM-13: the real ops dashboards (support board + revenue panel) —
// replaces the `dashboards` nav item's ComingSoonView placeholder.
const DashboardsView = () => import('@/views/dashboards/DashboardsView.vue')
// CRM-8's non-Flutter half (Kirana-oj1u): the help-centre article editor —
// replaces the `help` nav item's ComingSoonView placeholder, same
// graduation `customers`/`billing`/`support` already went through above.
const HelpArticlesView = () => import('@/views/help/HelpArticlesView.vue')
// CRM-12's real feature-status board editor — replaces the `features` nav
// item's ComingSoonView placeholder. Not the same screen as `flags` (that
// stays a placeholder; see nav-items.ts's doc comment on the two entries).
const FeaturesView = () => import('@/views/features/FeaturesView.vue')
// Kirana-6ipq's staff pre-provisioning screen — the `staff` nav item has no
// placeholder predecessor (it's new, not a graduation like the others above).
const StaffView = () => import('@/views/staff/StaffView.vue')
// Kirana-2uwu's real MS-4 status-platform admin screen (components,
// incidents, maintenance) — replaces the `status` nav item's
// ComingSoonView placeholder, same graduation `customers`/`billing`/
// `support`/`dashboards`/`help`/`features`/`staff` already went through
// above. The backend (CRM-9) and public status site (CRM-10) were already
// live; this was the missing staff-facing piece.
const StatusView = () => import('@/views/status/StatusView.vue')
// Kirana-h5bd's real waitlist signups admin panel — replaces the
// `waitlist` nav item's ComingSoonView placeholder, same graduation
// `customers`/`billing`/`support`/`dashboards`/`help`/`features`/`staff`/
// `status` already went through above.
const WaitlistView = () => import('@/views/waitlist/WaitlistView.vue')

// One gated child route per NAV_ITEMS entry — deriving this from the same
// list the sidebar renders means a nav item and its route/permission can
// never drift out of sync. `customers`/`billing` get their real MS-2
// screens, `support` gets CRM-6's real agent inbox, `dashboards` gets
// CRM-13's real ops dashboards, `help` gets CRM-8's real article editor,
// `features` gets CRM-12's real board editor, `status` gets Kirana-2uwu's
// real MS-4 admin screen, `waitlist` gets Kirana-h5bd's real signups panel;
// every other section is still the generic "coming soon" placeholder
// (later milestones), going through the same per-route permission guard
// either way.
function componentForNavKey(key: string) {
  if (key === 'customers') return CustomersView
  if (key === 'billing') return BillingView
  if (key === 'support') return SupportInboxView
  if (key === 'dashboards') return DashboardsView
  if (key === 'help') return HelpArticlesView
  if (key === 'features') return FeaturesView
  if (key === 'staff') return StaffView
  if (key === 'status') return StatusView
  if (key === 'waitlist') return WaitlistView
  return ComingSoonView
}

const navChildren: RouteRecordRaw[] = NAV_ITEMS.map((item) => ({
  path: item.key,
  name: item.routeName,
  component: componentForNavKey(item.key),
  meta: {
    permissions: [item.permission],
    navKey: item.key,
    titleKey: item.labelKey,
  },
}))

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    // The ONLY route the guard lets through without a session — see
    // router/guards.ts. Everything below (including /forbidden and the 404
    // catch-all) requires `useSessionStore().isAuthenticated`, which this
    // scaffold never sets, proving the guard denies-by-default.
    meta: { public: true, titleKey: 'login.title' },
  },
  // The rest of the real staffauth ceremony (OIDC callback -> passkey ->
  // MFA): all `meta.public`, same rationale as `/login` — no
  // `StaffPrincipal` exists yet at any of these steps, only a
  // server-tracked `ceremony_id` (see `stores/login-ceremony.ts`).
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: OidcCallbackView,
    meta: { public: true, titleKey: 'authCallback.title' },
  },
  {
    path: '/auth/passkey',
    name: 'auth-passkey',
    component: PasskeyStepView,
    meta: { public: true, titleKey: 'passkeyStep.title' },
  },
  {
    path: '/auth/mfa',
    name: 'auth-mfa',
    component: MfaStepView,
    meta: { public: true, titleKey: 'mfaStep.title' },
  },
  {
    path: '/',
    component: AppShellLayout,
    redirect: { name: 'customers' },
    children: [
      ...navChildren,
      // Contract C2 (docs/specs/contracts/c2-customer360-embed.md)'s deep-link
      // shape from a support ticket is the literal path
      // `/customers/{userId}?fromTicket={ticketId}` — a sibling route to the
      // bare `customers` nav entry (same CustomersView component; it reads
      // `route.params.userId` to open the profile drawer), mirroring how
      // `support/:id` sits alongside the `support` nav entry above.
      {
        path: 'customers/:userId',
        name: 'customer-profile',
        component: CustomersView,
        meta: {
          permissions: ['customer:read'],
          navKey: 'customers',
          titleKey: 'nav.customers',
        },
      },
      {
        path: 'support/:id',
        name: 'support-ticket',
        component: SupportTicketWorkspaceView,
        meta: {
          permissions: ['support:read'],
          navKey: 'support',
          titleKey: 'nav.support',
        },
      },
      {
        path: 'forbidden',
        name: 'forbidden',
        component: ForbiddenView,
        meta: { titleKey: 'forbidden.title' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { titleKey: 'notFound.title' },
  },
]
